"use client"
import { useInView } from "react-intersection-observer"
import { useState } from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { useEffect, useRef } from "react"

import "../assets/css/product-overview.css"
import capsium  from "../assets/images/products/Capsicum.webp"
import ScotchBonnet from "../assets/images/products/Scotch-Bonnet.webp"
import minch from "../assets/images/products/minch.webp"
import fruits from "../assets/images/products/fruits.webp"
import brinjal from "../assets/images/products/brinjal.webp"
import corn from "../assets/images/products/corn.webp"
import cassava from "../assets/images/products/cassava.webp"
import okra from "../assets/images/products/Oakra.webp"

export default function ProductOverview() {
  const { ref, inView } = useInView({
    threshold: 0.3,
    triggerOnce: true,
  })

  const products = [
    {
      name: "Scotch Bonnet Chili",
      image: ScotchBonnet,
      description:
        "Known for its intense heat and sweet, fruity flavor profile. Our Scotch Bonnets are carefully cultivated to ensure perfect balance of spice and taste.",
      color: "#dc2626", // red
    },
    {
      name: "Minch",
      image: minch,
      description:
        "A versatile leafy green, Minch is prized for its mild flavor and tender texture. Perfect for salads, stir-fries, and soups, it’s rich in vitamins and minerals.",
      color: "#16a34a", // green
    },
    {
      name: "Fruits",
      image: fruits,
      description:
        "Juicy, refreshing and sweet. Our fruits are grown to perfection, offering the perfect balance of sweetness and hydration.",
      color: "#f97316", // orange
    },
    {
      name: "Brinjal (Eggplant)",
      image: brinjal,
      description:
        "Our specialty fruit brinjals are tender with minimal seeds, perfect for a variety of culinary applications from grilling to curries.",
      color: "#8b5cf6", // purple
    }, {
      name: "Bell Peppers",
      image: capsium,
      description:
        "Vibrant, crisp and sweet. Our bell peppers come in a rainbow of colors, each offering a unique flavor profile and packed with nutrients.",
      color: "#16a34a", // green
    },
    {
      name: "Corn",
      image: corn,
      description:
        "Naturally sweet and tender. Our corn is harvested at peak ripeness to ensure maximum flavor and nutritional value.",
      color: "#eab308", // yellow
    },
    {
    name: "Cassava",
    image: cassava,
    description:
      "Starchy and versatile, our cassava roots are freshly harvested, offering excellent texture for a variety of culinary applications from fries to traditional dishes.",
    color: "#92400e", // brown/amber
  },
  {
    name: "Okra",
    image: okra,
    description:
      "Tender and uniquely textured. Our okra pods are picked young for optimal flavor, perfect for stews, curries, and traditional dishes around the world.",
    color: "#65a30d", // lime green
  },
  ]

  const [activeProduct, setActiveProduct] = useState(0)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    // Clear any existing interval
    if (intervalRef.current) clearInterval(intervalRef.current)
    // Set up auto-slide every 4 seconds
    intervalRef.current = setInterval(() => {
      setActiveProduct((prev) => (prev + 1) % products.length)
    }, 4000)
    // Clean up on unmount
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [products.length])

  const nextProduct = () => {
    setActiveProduct((prev) => (prev + 1) % products.length)
  }

  const prevProduct = () => {
    setActiveProduct((prev) => (prev - 1 + products.length) % products.length)
  }

  return (
    <section id="products" ref={ref} className="product-section">
      <div className="product-container">
        <div className="section-header">
          <h2 className={`section-title ${inView ? "visible" : ""}`}>Our Fresh Produce</h2>
          <div
            className={`section-divider ${inView ? "visible" : ""}`}
            style={{ backgroundColor: products[activeProduct].color }}
          ></div>
        </div>

        <div className="product-showcase">
          {/* Product Carousel */}
          <div className={`product-carousel ${inView ? "visible" : ""}`}>
            <button onClick={prevProduct} className="carousel-control carousel-prev" aria-label="Previous product">
              <ChevronLeft size={24} />
            </button>

            <div className="carousel-container">
              {products.map((product, index) => (
                <div
                  key={index}
                  className={`carousel-item ${activeProduct === index ? "active" : ""}`}
                  style={{ transform: `translateX(${(index - activeProduct) * 100}%)` }}
                >
                  <div className="product-image-wrapper">
                    <img
                      src={product.image || "/placeholder.svg"}
                      alt={product.name}
                      
                      className="product-image"
                      style={{ objectFit: "cover" }}
                      
                    />
                    <div className="product-name-overlay" style={{ backgroundColor: `${product.color}99` }}>
                      <h3>{product.name}</h3>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <button onClick={nextProduct} className="carousel-control carousel-next" aria-label="Next product">
              <ChevronRight size={24} />
            </button>
          </div>

          {/* Product Info */}
          <div className={`product-content ${inView ? "visible" : ""}`}>
            <h3 className="product-title" style={{ color: products[activeProduct].color }}>
              {products[activeProduct].name}
            </h3>

            <p className="product-description">{products[activeProduct].description}</p>

            <ul className="product-features">
              <li className="feature-item">
                <div className="feature-icon" style={{ backgroundColor: products[activeProduct].color }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M5 13L9 17L19 7"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <p className="feature-text">
                  <span className="feature-highlight">Grown in Sri Lanka</span> - Cultivated in semi-greenhouses with
                  sustainable farming practices
                </p>
              </li>
              <li className="feature-item">
                <div className="feature-icon" style={{ backgroundColor: products[activeProduct].color }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M5 13L9 17L19 7"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <p className="feature-text">
                  <span className="feature-highlight">Premium Quality</span> - Handpicked at peak ripeness for maximum
                  flavor and nutrition
                </p>
              </li>
              <li className="feature-item">
                <div className="feature-icon" style={{ backgroundColor: products[activeProduct].color }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M5 13L9 17L19 7"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <p className="feature-text">
                  <span className="feature-highlight">Sustainable Practices</span> - Environmentally friendly farming
                  methods that preserve soil health
                </p>
              </li>
              <li className="feature-item">
                <div className="feature-icon" style={{ backgroundColor: products[activeProduct].color }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M5 13L9 17L19 7"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <p className="feature-text">
                  <span className="feature-highlight">Community Support</span> - Nurtured by the hard work and
                  dedication of local farmers
                </p>
              </li>
            </ul>

            <div className="product-indicators">
              {products.map((_, index) => (
                <button
                  key={index}
                  className={`indicator ${activeProduct === index ? "active" : ""}`}
                  style={{
                    backgroundColor: activeProduct === index ? products[index].color : "#e5e7eb",
                    width: activeProduct === index ? "2rem" : "1rem",
                  }}
                  onClick={() => setActiveProduct(index)}
                  aria-label={`View ${products[index].name}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Background decoration */}
      <div className="bg-circle bg-circle-1" style={{ backgroundColor: `${products[activeProduct].color}22` }}></div>
      <div className="bg-circle bg-circle-2" style={{ backgroundColor: `${products[activeProduct].color}11` }}></div>
    </section>
  )
}
